package com.advaita.cloud.drive.ui.api

import kotlinx.browser.sessionStorage
import org.w3c.dom.Storage
import org.w3c.dom.get

/** Minimal key/value seam so [LoginLoopGuard] is testable off-browser (jsTest runs under Node). */
interface KeyValueStore {
    fun get(key: String): String?
    fun set(key: String, value: String)
    fun remove(key: String)
}

/** Backs the guard with the real per-tab sessionStorage. */
class BrowserSessionStore(private val storage: Storage = sessionStorage) : KeyValueStore {
    override fun get(key: String): String? = storage[key]
    override fun set(key: String, value: String) = storage.setItem(key, value)
    override fun remove(key: String) = storage.removeItem(key)
}

/**
 * ADV-1155: what [LoginLoopGuard.recordBounce] decided to do about a bounce.
 *
 * There is deliberately no "already in flight, do nothing" case. An earlier revision had one,
 * and it reintroduced the ADV-443 failure class from a new direction: because that case
 * declined to navigate, any page load whose navigation did not commit — the user clicking
 * "Stay" on the uploads-running `beforeunload` prompt, an unreachable /auth/login, a bfcache
 * restore — left the guard permanently answering "do nothing" and the user could not sign in
 * without a manual reload. A repeated bounce within one page load now re-issues the SAME
 * decision instead, so the navigation is retried while the budget is not spent twice.
 */
enum class BounceDecision {
    /** Navigate to /auth/login now. */
    REDIRECT,

    /** We are clearly looping across page loads — stop, fall through to LoginGate. */
    GIVE_UP,
}

/**
 * Caps consecutive "401 → clear token → /auth/login" bounces so a persistent 401 cannot
 * become an endless redirect loop.
 *
 * ADV-443: the server-side fixes stop an az2 outage producing a 401 at all (it is a 503
 * now), but any OTHER 401 source still triggers the same bounce — and because az2
 * reissues the same access token for a live session, a re-login can hand back the very
 * credential that was just rejected. In prod that looped for 5 minutes and ~516 requests.
 * Past [maxBounces] we stop redirecting and let the app fall through to LoginGate, which
 * explains what happened rather than spinning.
 *
 * State lives in sessionStorage: per-tab, and it must survive the redirect itself.
 */
class LoginLoopGuard(
    private val store: KeyValueStore,
    private val maxBounces: Int = DEFAULT_MAX_BOUNCES,
) {
    // ADV-1155: the decision already taken during THIS page load, memoised in memory and
    // deliberately NOT in sessionStorage. `App.kt` runs three authenticated calls
    // (loadCurrentUser, loadAuthConfig, refresh) in one LaunchedEffect and a dead token 401s all
    // three; assigning window.location.href does not stop the coroutine already running. Without
    // this, all three recorded a bounce and one page load spent the whole persisted budget.
    //
    // Memoising the DECISION rather than flying a "redirect in flight" flag is the important
    // part: a repeat caller gets the same instruction and therefore re-issues the same
    // navigation. Re-assigning an identical pending navigation is a no-op in the browser, but it
    // means a navigation that never commits gets retried instead of stranding the guard.
    //
    // The guard is a singleton constructed once by AuthApi, so this dies with the JS context —
    // which is exactly "one bounce per page load" — while the persisted counter below survives.
    private var decisionThisLoad: BounceDecision? = null

    // ADV-443: every storage access is guarded. Safari private browsing has historically thrown
    // QuotaExceededError from setItem with a zero quota, and storage-partitioned iframes throw
    // SecurityError. Unguarded, that exception escaped recordBounce() -> goToLogin() and the 401
    // stopped redirecting AT ALL — a loop guard that prevents logins is worse than no guard. So
    // storage failure degrades to "count 0, always allow the redirect".
    private fun count(): Int =
        runCatching { store.get(BOUNCE_KEY)?.toIntOrNull() }.getOrNull() ?: 0

    /**
     * Records a bounce to the login page and says what to do about it.
     *
     * ADV-1155: the second and third 401 of the same page load must not each cost a unit of
     * budget — that is what tripped the dead-end page after a single load. They get the first
     * call's decision back, unchanged and uncounted, so the caller re-issues the same
     * navigation. Idempotent within a page load; the counter only moves on the first call.
     */
    fun recordBounce(): BounceDecision {
        decisionThisLoad?.let { return it }

        val next = count() + 1
        val stored = runCatching { store.set(BOUNCE_KEY, next.toString()) }.isSuccess
        // Invariant I1 (ADV-443): storage failure must never block a login redirect.
        val decision = when {
            !stored -> BounceDecision.REDIRECT
            next > maxBounces -> BounceDecision.GIVE_UP
            else -> BounceDecision.REDIRECT
        }
        decisionThisLoad = decision
        return decision
    }

    /** True once [recordBounce] has given up — LoginGate uses this to explain itself. */
    fun isTripped(): Boolean = count() > maxBounces

    /** Called whenever an authenticated request succeeds: the loop, if any, is over. */
    fun reset() {
        runCatching { store.remove(BOUNCE_KEY) }
        decisionThisLoad = null
    }

    companion object {
        const val BOUNCE_KEY = "drive.loginBounces"

        /**
         * Three allows for genuinely legitimate re-logins (expiry mid-session, a token
         * revoked in another tab) before the guard gives up.
         *
         * ADV-1155 changed what this costs. Under the old per-request counting three bounces
         * could be spent inside one page load, so the guard tripped "within a second or two" —
         * which is precisely the bug. Counting per page load means tripping now takes three
         * full redirect round trips through az2, i.e. seconds each. That is the intended
         * trade: a slower guard that cannot fire spuriously.
         */
        const val DEFAULT_MAX_BOUNCES = 3
    }
}
